import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Document, Page, Thumbnail, pdfjs } from 'react-pdf';

import { Spinner } from '@core/components/ui';
import { logger } from '@core/utils';

import { getPdfSelectionInfo } from '../utils/pdfSelection';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const PAGE_PADDING = 32; // horizontal padding (py-4 px ~ 16 each side)
const THUMBNAIL_WIDTH = 120;

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * PDF viewer with thumbnail sidebar, search, and zoom controls.
 */
export function PdfViewer({
  url,
  initialSidebarOpen = false,
  initialPage = null,
  onPageChange,
  onTextSelected,
}) {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1);
  const [loadError, setLoadError] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const [containerWidth, setContainerWidth] = useState(0);
  const consumedInitialPageRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [pageTexts, setPageTexts] = useState({});
  const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);

  const pageRefs = useRef({});
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);
  const observerRef = useRef(null);
  const widthObserverRef = useRef(null);
  const searchInputRef = useRef(null);

  const setContentRef = useCallback((el) => {
    contentRef.current = el;
    if (widthObserverRef.current) {
      widthObserverRef.current.disconnect();
      widthObserverRef.current = null;
    }
    if (!el) return;
    const update = () => {
      setContainerWidth(Math.max(0, el.clientWidth - PAGE_PADDING));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    widthObserverRef.current = observer;
  }, []);

  const matches = useMemo(() => {
    if (!searchQuery || !numPages) return [];
    const query = searchQuery.toLowerCase();
    const found = [];
    for (let p = 1; p <= numPages; p++) {
      const text = (pageTexts[p] || '').toLowerCase();
      let idx = text.indexOf(query);
      while (idx !== -1) {
        found.push({ page: p });
        idx = text.indexOf(query, idx + 1);
      }
    }
    return found;
  }, [searchQuery, pageTexts, numPages]);

  useEffect(() => {
    setCurrentMatchIdx(matches.length > 0 ? 0 : -1);
  }, [matches]);

  const onDocumentLoadSuccess = useCallback(async (pdf) => {
    setNumPages(pdf.numPages);
    const texts = {};
    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        texts[i] = textContent.items.map((item) => item.str).join(' ');
      } catch (err) {
        logger.error(`Failed to extract text from page ${i}:`, err);
        texts[i] = '';
      }
    }
    setPageTexts(texts);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    setLoadError(error);
  }, []);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const fitToWidth = useCallback(() => {
    setScale(1);
  }, []);

  const scrollToPage = useCallback((pageNumber) => {
    setActivePage(pageNumber);
    const el = pageRefs.current[pageNumber];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    if (!numPages || !contentRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let maxRatio = 0;
        let mostVisible = null;
        for (const entry of entries) {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisible = Number(entry.target.dataset.page);
          }
        }
        if (mostVisible) {
          setActivePage(mostVisible);
        }
      },
      {
        root: contentRef.current,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    for (let i = 1; i <= numPages; i++) {
      const el = pageRefs.current[i];
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [numPages]);

  // Report the visible page to the parent (used for canvas page context).
  useEffect(() => {
    if (numPages) onPageChange?.(activePage, numPages);
  }, [activePage, numPages, onPageChange]);

  // One-shot jump to a requested page once the pages have laid out.
  useEffect(() => {
    if (!initialPage || !numPages || containerWidth <= 0) return;
    if (consumedInitialPageRef.current === initialPage) return;
    const el = pageRefs.current[initialPage];
    if (!el) return;
    consumedInitialPageRef.current = initialPage;
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
    setActivePage(initialPage);
  }, [initialPage, numPages, containerWidth]);

  const handleMouseUp = useCallback(() => {
    if (!onTextSelected) return;
    // Defer so the browser finalizes the selection before we read it.
    setTimeout(() => {
      onTextSelected(getPdfSelectionInfo(window.getSelection(), contentRef.current));
    }, 0);
  }, [onTextSelected]);

  useEffect(() => {
    if (!onTextSelected) return undefined;
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) onTextSelected(null);
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [onTextSelected]);

  useEffect(() => {
    if (!sidebarRef.current || !sidebarOpen) return;
    const thumb = sidebarRef.current.querySelector(
      `[data-thumb="${activePage}"]`
    );
    if (thumb) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activePage, sidebarOpen]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const goToMatch = useCallback(
    (idx) => {
      if (idx < 0 || idx >= matches.length) return;
      setCurrentMatchIdx(idx);
      scrollToPage(matches[idx].page);
    },
    [matches, scrollToPage]
  );

  const nextMatch = useCallback(() => {
    if (!matches.length) return;
    goToMatch((currentMatchIdx + 1) % matches.length);
  }, [currentMatchIdx, matches.length, goToMatch]);

  const prevMatch = useCallback(() => {
    if (!matches.length) return;
    goToMatch((currentMatchIdx - 1 + matches.length) % matches.length);
  }, [currentMatchIdx, matches.length, goToMatch]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        if (e.shiftKey) {
          prevMatch();
        } else {
          nextMatch();
        }
      }
    },
    [nextMatch, prevMatch]
  );

  const customTextRenderer = useMemo(() => {
    if (!searchQuery) return undefined;
    const escaped = escapeRegex(searchQuery);
    const regex = new RegExp(escaped, 'gi');

    return ({ str }) => {
      const parts = str.split(regex);
      const matched = str.match(regex);
      if (!matched) return escapeHtml(str);

      let result = '';
      for (let i = 0; i < parts.length; i++) {
        result += escapeHtml(parts[i]);
        if (i < matched.length) {
          result +=
            '<mark style="background-color: rgba(250, 204, 21, 0.4); border-radius: 2px;">' +
            escapeHtml(matched[i]) +
            '</mark>';
        }
      }
      return result;
    };
  }, [searchQuery]);

  const setPageRef = useCallback(
    (pageNumber) => (el) => {
      pageRefs.current[pageNumber] = el;
    },
    []
  );

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-[var(--color-danger)]">
        <Icon icon="mdi:alert-circle" className="size-12 mb-2" />
        <p className="text-sm">{loadError.message || t('dataroom.viewer.pdfLoadFailed')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 py-2 px-4 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] shrink-0">
        <button
          onClick={() => setSidebarOpen((prev) => !prev)}
          className={`p-1 rounded cursor-pointer transition-colors ${
            sidebarOpen
              ? 'bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]'
              : 'hover:bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]'
          }`}
          aria-label={sidebarOpen ? t('dataroom.viewer.hideSidebar') : t('dataroom.viewer.showSidebar')}
        >
          <Icon icon="mdi:page-layout-sidebar-left" className="size-5" />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={scale <= MIN_ZOOM}
            className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('dataroom.viewer.zoomOut')}
          >
            <Icon icon="mdi:minus" className="size-5" />
          </button>
          <span className="text-sm font-medium w-16 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= MAX_ZOOM}
            className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('dataroom.viewer.zoomIn')}
          >
            <Icon icon="mdi:plus" className="size-5" />
          </button>
          <button
            onClick={fitToWidth}
            className="p-1 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer"
            aria-label={t('dataroom.viewer.fitToWidth')}
          >
            <Icon icon="mdi:fit-to-page-outline" className="size-5" />
          </button>
          {numPages && (
            <span className="text-xs text-[var(--color-text-secondary)] ml-1">
              {t('dataroom.viewer.pageCount', { count: numPages })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <div className="relative flex items-center">
            <Icon
              icon="mdi:magnify"
              className="absolute left-2 size-4 text-[var(--color-text-muted)] pointer-events-none"
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t('dataroom.viewer.searchPlaceholder')}
              className="pl-7 pr-7 py-1 text-sm border border-[var(--color-border)] rounded-md w-40 focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 p-0.5 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer"
                aria-label={t('dataroom.viewer.clearSearch')}
              >
                <Icon icon="mdi:close" className="size-3.5 text-[var(--color-text-muted)]" />
              </button>
            )}
          </div>
          {searchQuery && (
            <>
              <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap min-w-[4rem] text-center">
                {matches.length > 0
                  ? t('dataroom.viewer.matchPosition', { current: currentMatchIdx + 1, total: matches.length })
                  : t('dataroom.viewer.noResults')}
              </span>
              <button
                onClick={prevMatch}
                disabled={!matches.length}
                className="p-0.5 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('dataroom.viewer.previousMatch')}
              >
                <Icon icon="mdi:chevron-up" className="size-4" />
              </button>
              <button
                onClick={nextMatch}
                disabled={!matches.length}
                className="p-0.5 rounded hover:bg-[var(--color-bg-muted)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('dataroom.viewer.nextMatch')}
              >
                <Icon icon="mdi:chevron-down" className="size-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content: sidebar + pages */}
      <div className="flex-1 overflow-hidden">
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          className="flex h-full"
          loading={
            <div className="flex items-center justify-center flex-1">
              <Spinner size="lg" />
            </div>
          }
        >
          {sidebarOpen && numPages && (
            <div
              ref={sidebarRef}
              className="w-40 border-r border-[var(--color-border)] bg-[var(--color-bg-subtle)] overflow-y-auto shrink-0 py-3 px-2 flex flex-col gap-2"
            >
              {Array.from({ length: numPages }, (_, i) => {
                const pageNum = i + 1;
                const isActive = activePage === pageNum;
                return (
                  <button
                    key={pageNum}
                    data-thumb={pageNum}
                    onClick={() => scrollToPage(pageNum)}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded-lg cursor-pointer transition-all ${
                      isActive
                        ? 'bg-[var(--color-accent-subtle)] ring-2 ring-[var(--color-accent)]'
                        : 'hover:bg-[var(--color-bg-muted)]'
                    }`}
                  >
                    <div className="shadow-sm rounded overflow-hidden">
                      <Thumbnail pageNumber={pageNum} width={THUMBNAIL_WIDTH} />
                    </div>
                    <span
                      className={`text-xs ${
                        isActive
                          ? 'text-[var(--color-accent)] font-semibold'
                          : 'text-[var(--color-text-secondary)]'
                      }`}
                    >
                      {pageNum}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div
            ref={setContentRef}
            onMouseUp={handleMouseUp}
            onScroll={onTextSelected ? () => onTextSelected(null) : undefined}
            className="flex-1 overflow-auto bg-[var(--color-bg-muted)]"
          >
            {numPages && containerWidth > 0 &&
              Array.from({ length: numPages }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <div
                    key={pageNum}
                    ref={setPageRef(pageNum)}
                    data-page={pageNum}
                    className="flex justify-center py-4"
                  >
                    <div className="shadow-lg">
                      <Page
                        pageNumber={pageNum}
                        width={containerWidth * scale}
                        customTextRenderer={customTextRenderer}
                        loading={
                          <div
                            className="flex items-center justify-center bg-white"
                            style={{
                              height: 400,
                              width: containerWidth * scale,
                            }}
                          >
                            <Spinner size="md" />
                          </div>
                        }
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </Document>
      </div>
    </div>
  );
}

PdfViewer.propTypes = {
  url: PropTypes.string.isRequired,
  initialSidebarOpen: PropTypes.bool,
  initialPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onTextSelected: PropTypes.func,
};
